
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiJson, ApiError } from "../../../lib/api";

import { getTenantUuid } from "../../ingress/tenant"; // adjust path if needed

const TENANT_UUID = getTenantUuid();

type IngressAppRow = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  is_published: boolean;
  flow_id: string;
  version_id: string;
  auth_mode?: string | null;
  rate_limit_rps?: number | null;
  created_at?: string;
  updated_at?: string;
};

function normalizeList(payload: any): IngressAppRow[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.apps)) return payload.apps;
  }
  return [];
}

export default function AdminIngressAppsPage() {
  const [rows, setRows] = useState<IngressAppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    slug: "",
    name: "",
    flow_id: "",
    version_id: "",
    auth_mode: "tenant",
    rate_limit_rps: 5,
    is_published: true,
    description: "",
  });

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const data = await apiJson<any>(`/v1/admin/ingress/apps?tenant_id=${TENANT_UUID}`);
      setRows(normalizeList(data));
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function create() {
    setErr(null);
    try {
      const payload = {
        slug: form.slug.trim(),
        name: form.name.trim(),
        description: form.description || null,
        flow_id: form.flow_id.trim(),
        version_id: form.version_id.trim(),
        auth_mode: form.auth_mode,
        rate_limit_rps: Number(form.rate_limit_rps || 5),
        is_published: !!form.is_published,
      };
      const res = await apiJson<any>(`/v1/admin/ingress/apps?tenant_id=${TENANT_UUID}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setCreateOpen(false);
      await load();
      // navigate? keep simple
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  }

  const publishedCount = useMemo(() => rows.filter(r => r.is_published).length, [rows]);

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Ingress Apps</h2>
          <div className="text-sm text-gray-600 mt-1">
            {rows.length} total · {publishedCount} published
          </div>
        </div>
        <button
          className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
          onClick={() => setCreateOpen((v) => !v)}
        >
          New App
        </button>
      </div>

      {err && <div className="mt-4 text-sm text-red-600 whitespace-pre-wrap">{err}</div>}

      {createOpen && (
        <div className="mt-4 border rounded bg-white p-4">
          <div className="font-medium">Create Ingress App</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <label className="text-sm">
              <div className="text-gray-700">Slug</div>
              <input className="w-full border rounded px-3 py-2" value={form.slug} onChange={(e)=>setForm(p=>({...p, slug:e.target.value}))} placeholder="my-app" />
            </label>
            <label className="text-sm">
              <div className="text-gray-700">Name</div>
              <input className="w-full border rounded px-3 py-2" value={form.name} onChange={(e)=>setForm(p=>({...p, name:e.target.value}))} placeholder="My App" />
            </label>
            <label className="text-sm md:col-span-2">
              <div className="text-gray-700">Description</div>
              <input className="w-full border rounded px-3 py-2" value={form.description} onChange={(e)=>setForm(p=>({...p, description:e.target.value}))} placeholder="Optional" />
            </label>
            <label className="text-sm">
              <div className="text-gray-700">Flow ID</div>
              <input className="w-full border rounded px-3 py-2" value={form.flow_id} onChange={(e)=>setForm(p=>({...p, flow_id:e.target.value}))} placeholder="UUID" />
            </label>
            <label className="text-sm">
              <div className="text-gray-700">Version ID</div>
              <input className="w-full border rounded px-3 py-2" value={form.version_id} onChange={(e)=>setForm(p=>({...p, version_id:e.target.value}))} placeholder="UUID" />
            </label>
            <label className="text-sm">
              <div className="text-gray-700">Auth Mode</div>
              <select className="w-full border rounded px-3 py-2" value={form.auth_mode} onChange={(e)=>setForm(p=>({...p, auth_mode:e.target.value}))}>
                <option value="tenant">tenant</option>
                <option value="none">none</option>
              </select>
            </label>
            <label className="text-sm">
              <div className="text-gray-700">Rate Limit (rps)</div>
              <input type="number" className="w-full border rounded px-3 py-2" value={form.rate_limit_rps} onChange={(e)=>setForm(p=>({...p, rate_limit_rps:Number(e.target.value)}))} />
            </label>
            <label className="text-sm flex items-center gap-2 mt-2">
              <input type="checkbox" checked={form.is_published} onChange={(e)=>setForm(p=>({...p, is_published:e.target.checked}))}/>
              <span>Published</span>
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <button className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={create}>Create</button>
            <button className="px-3 py-2 rounded border" onClick={()=>setCreateOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-4 border rounded bg-white overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-50">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Slug</div>
          <div className="col-span-2">Published</div>
          <div className="col-span-2">Rate</div>
          <div className="col-span-2">Flow / Version</div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No apps found.</div>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t text-sm">
              <div className="col-span-3">
                <Link className="text-blue-700 hover:underline" to={`/app/admin/ingress/apps/${r.id}`}>{r.name}</Link>
                {r.description && <div className="text-xs text-gray-500 mt-1">{r.description}</div>}
              </div>
              <div className="col-span-3"><code className="text-xs">{r.slug}</code></div>
              <div className="col-span-2">{r.is_published ? "✅" : "—"}</div>
              <div className="col-span-2">{r.rate_limit_rps ?? "—"}</div>
              <div className="col-span-2">
                <div className="text-xs text-gray-600 truncate" title={r.flow_id}>{r.flow_id}</div>
                <div className="text-xs text-gray-600 truncate" title={r.version_id}>{r.version_id}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
